import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Container,
  Paper,
  Link,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Helmet } from 'react-helmet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import EmailIcon from '@mui/icons-material/Email';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { styled } from '@mui/material/styles';
import validator from 'validator';
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup
} from 'firebase/auth';

// Components
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import Sidebar from '../../components/Sidebar/Sidebar';
import CustomModal from '../../components/CustomModal';
import CustomNoResponseModal from '../../components/CustomNoResponseModal';

// Assets
import googleicon from '../../../assets/img/google-icon.svg';
import appleicon from '../../../assets/img/appleLogo.png';

// Firebase and utilities
import { authen, provider, providerApple } from '../../firebase/firebase';
import { fetchCookieUsers } from '../../Utils/firebase-services';
import cookiePreferences from 'shared/Utils/CookiePreferenceApi/api';
import { environment } from '../../../../src/environments/environment';
import RemoteErrorLog from '../../Utils/api';

// Styles
import '../../scss/Form.scss';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3, 4),
  borderRadius: theme.spacing(2.5),
  border: '1px solid #e2e8f0',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  boxShadow: '0 2px 15px rgba(0, 0, 0, 0.06), 0 1px 8px rgba(0, 0, 0, 0.04)',
  marginBottom: theme.spacing(2.5),
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.2s ease',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #2A9235, #4CAF50)',
  },

  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 10px rgba(0, 0, 0, 0.05)',
    transform: 'translateY(-1px)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: '#fafbfc',
    transition: 'all 0.2s ease',
    fontSize: '0.95rem',

    '&:hover': {
      backgroundColor: '#ffffff',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2A9235',
      }
    },

    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2A9235',
        borderWidth: '2px',
      }
    }
  },

  '& .MuiInputLabel-root': {
    fontSize: '0.95rem',
    fontWeight: 500,
    '&.Mui-focused': {
      color: '#2A9235',
    }
  },

  '& .MuiFormHelperText-root': {
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.5),
  }
}));

const StyledSocialButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '56px',
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  gap: theme.spacing(2),
  borderRadius: theme.spacing(2),
  border: '2px solid #e2e8f0',
  background: '#ffffff',
  color: '#1a1a1a',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)',
  
  '&:hover:not(:disabled)': {
    borderColor: '#2A9235',
    background: '#ffffff',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 20px rgba(42, 146, 53, 0.15)',
    
    '& .social-logo': {
      transform: 'scale(1.05)',
    }
  },

  '&:disabled': {
    opacity: 0.5,
    background: '#f5f5f5',
    cursor: 'not-allowed',
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2.5),
  padding: theme.spacing(1.75, 4),
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
  color: 'white',
  border: 'none',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 3px 15px rgba(42, 146, 53, 0.2)',

  '&:hover:not(:disabled)': {
    background: 'linear-gradient(135deg, #1e7a2b 0%, #43a047 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 5px 20px rgba(42, 146, 53, 0.25)',
  },

  '&:disabled': {
    background: '#cccccc',
    color: '#666666',
    cursor: 'not-allowed',
  }
}));

const BackButton = styled(Button)(({ theme }) => ({
  color: '#2A9235',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(0.75, 1.5),
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.2s ease',
  fontSize: '0.9rem',
  '&:hover': {
    backgroundColor: 'rgba(42, 146, 53, 0.08)',
    transform: 'translateX(-2px)',
  }
}));

const IndividualLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // State management - matching LoginSub.tsx
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Cookie and modal states from LoginSub.tsx
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isQuestionModalVisible, setQuestionModalVisible] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState('');
  const [firebaseUser, setFirebaseUser] = useState<any>(null);
  const [firebaseUserProvider, setFirebaseUserProvider] = useState('');
  const [isCookieModalVisible, setIsCookieModalVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    navigate('/login');
  };

  const handleToggleShowPassword = () => setShowPassword((s) => !s);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // Helper function to handle redirect after successful login
  const handleSuccessfulLogin = () => {
    const state = location.state as any;
    if (state?.redirectTo === '/profile' && state?.openMembershipTab) {
      navigate('/profile', {
        state: {
          openMembershipTab: true,
          tabIndex: state.tabIndex || 3
        }
      });
    } else {
      navigate('/home');
    }
  };

  // Load saved credentials if remember me was checked
  useEffect(() => {
    const loadCredentials = async () => {
      const storedCredentials = localStorage.getItem('userCredentials');
      if (storedCredentials) {
        const credentials = JSON.parse(storedCredentials);
        if (credentials.loginType === 'email') {
          setRememberMe(true);
          setEmail(credentials.email || '');
          setPassword(credentials.password || '');
          if (credentials.email) validateEmail(credentials.email);
          if (credentials.password) validatePassword(credentials.password);
        }
      }
    };

    loadCredentials();
  }, []);

  // Cookie handling effect - matching LoginSub.tsx
  useEffect(() => {
    if (firebaseUser) {
      hideCookieModal();
    }
  }, [firebaseUser]);

  // Email validation
  const validateEmail = (input: string) => {
    setEmail(input);
    if (!input) {
      setEmailError('Email is required.');
      setIsEmailValid(false);
      return;
    }

    if (validator.isEmail(input)) {
      setIsEmailValid(true);
      setEmailError('');
    } else {
      setIsEmailValid(false);
      setEmailError('Please enter a valid email address.');
    }
  };

  // Password validation
  const validatePassword = (text: string) => {
    setPassword(text);
    if (text.length < 6) {
      setPasswordError('The password must be at least 6 characters long.');
      setIsPasswordValid(false);
    } else {
      setIsPasswordValid(true);
      setPasswordError('');
    }
  };

  // Cookie API call function - matching LoginSub.tsx
  const cookieApiCall = async (cookieInfo: any) => {
    const token = await (authen?.currentUser as any).getIdToken(true);
    const result = await cookiePreferences(
      cookieInfo,
      token,
      environment?.apiUrl
    );

    if (result === 'Error') {
      setQuestionModalVisible(true);
      setApiErrorMessage(
        "We're currently experiencing some issues. Please try again later."
      );
      setIsLoading(false);
      localStorage.removeItem('userData');
    } else {
      setIsLoading(false);
      handleSuccessfulLogin();
    }
  };

  // Hide cookie modal function - matching LoginSub.tsx
  const hideCookieModal = async () => {
    setIsCookieModalVisible(false);
    const cookieInfo = {
      uid: firebaseUser.uid,
      full_name: firebaseUser.displayName,
      is_anonymous: firebaseUser.isAnonymous,
      dob: null,
    };

    if (cookieInfo.uid) {
      setIsLoading(true);
      cookieApiCall(cookieInfo);
    }
  };

  // Hide modal function - matching LoginSub.tsx
  const hideModal = () => {
    setIsModalVisible(false);
    setQuestionModalVisible(false);
  };

  // Google Sign In - matching LoginSub.tsx logic exactly
  const googleSignIn = async () => {
    try {
      const result = await signInWithPopup(authen, provider);
      const googleCredentials = GoogleAuthProvider.credentialFromResult(result);
      const userInfo = signInWithCredential(authen, googleCredentials!);
      setIsLoading(true);
      localStorage.setItem('userData', JSON.stringify((await userInfo).user));
      const { uid, isAnonymous, displayName } = (await userInfo).user;
      const userCookiePreference = await fetchCookieUsers(uid);

      if (userCookiePreference === 'Error') {
        setQuestionModalVisible(true);
        setApiErrorMessage(
          "We're currently experiencing some issues. Please try again later."
        );
        setIsLoading(false);
        localStorage.removeItem('userData');
      } else if (userCookiePreference === false) {
        setFirebaseUser({ uid, isAnonymous, displayName });
      } else {
        setIsLoading(false);
        handleSuccessfulLogin();
      }
      setFirebaseUserProvider((await userInfo).user.providerData[0].providerId);
    } catch (error: any) {
      RemoteErrorLog(error, 'IndividualLogin.tsx');
    }
  };

  // Apple Sign In - matching LoginSub.tsx logic exactly
  const appleSignIn = async () => {
    try {
      providerApple.addScope('email');
      providerApple.addScope('name');
      const result = await signInWithPopup(authen, providerApple);
      const credential = OAuthProvider.credentialFromResult(result);
      const userInfo = signInWithCredential(authen, credential!);
      setIsLoading(true);
      localStorage.setItem('userData', JSON.stringify((await userInfo).user));
      const { uid, isAnonymous, displayName } = (await userInfo).user;
      const userCookiePreference = await fetchCookieUsers(uid);

      if (userCookiePreference !== true) {
        setFirebaseUser({ uid, isAnonymous, displayName } as any);
      } else {
        setIsLoading(false);
        handleSuccessfulLogin();
      }
      setFirebaseUserProvider((await userInfo).user.providerData[0].providerId);
    } catch (error: any) {
      RemoteErrorLog(error, 'IndividualLogin.tsx');
    }
  };

  // Email/Password Login - matching LoginSub.tsx logic
  const handleEmailLogin = async () => {
    setIsLoading(true);
    try {
      // Remove stored credentials if remember me is not checked
      const storedCredentials = localStorage.getItem('userCredentials');
      if (storedCredentials && !rememberMe) {
        localStorage.removeItem('userCredentials');
      }

      const userCredential = await signInWithEmailAndPassword(authen, email, password);
      const user = userCredential.user;

      // Save credentials if remember me is checked
      if (rememberMe) {
        localStorage.setItem('userCredentials', JSON.stringify({
          email,
          password,
          loginType: 'email'
        }));
      }

      if (user.emailVerified) {
        await user.getIdToken(true);
        const idTokenResult = await user.getIdTokenResult();
        localStorage.setItem('userData', JSON.stringify(userCredential.user));
        const userCookiePreference = await fetchCookieUsers(user.uid);
        const { uid, isAnonymous, displayName } = user;

        if (userCookiePreference === 'Error') {
          setQuestionModalVisible(true);
          setApiErrorMessage(
            "We're currently experiencing some issues. Please try again later."
          );
          setIsLoading(false);
          localStorage.removeItem('userData');
        } else {
          setIsLoading(false);
          if (idTokenResult.claims.indian_school_admin) {
            navigate('/school-admin-dashboard');
          } else {
            handleSuccessfulLogin();
          }
        }
      } else {
        setIsModalVisible(true);
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      RemoteErrorLog(error, 'IndividualLogin.tsx');
      setApiErrorMessage('The email or password entered is incorrect. Please try again.');
      setQuestionModalVisible(true);
    }
  };

  const isFormValid = () => {
    return isEmailValid && isPasswordValid;
  };

  return (
    <>
      <Helmet>
        <title>Login - Spelling Hero</title>
        <meta name="description" content="Login to your personal Spelling Hero account. Sign in with Google, Apple, or email to access spelling games and track your progress." />
        <meta name="keywords" content="login, personal login, individual login, google login, apple login, spelling games" />
      </Helmet>

      <header>
        <Navbar currentPage="login" />
      </header>

      <main className="main-container" style={{ paddingTop: 0 }}>
        <section className="quiz-signup">
          <Box sx={{ padding: 0 }}>
            <Grid container spacing={2}>
              <Grid
                item
                lg={5}
                xs={12}
                sm={12}
                sx={{ paddingLeft: 0 }}
                className="sidebar-top-spacing"
              >
                <Sidebar />
              </Grid>

              <Grid item xs={12} sm={12} lg={7} xl={7}>
                <Box sx={{
                  position: 'relative',
                  paddingLeft: { xs: 2, sm: 3, md: 4 },
                  paddingRight: { xs: 2, sm: 3, md: 4 },
                  paddingTop: { xs: 1, md: 0 },
                  paddingBottom: 4,
                  display: 'flex',
                  alignItems: { xs: 'center', md: 'flex-start' },
                  minHeight: { xs: 'auto', md: 'calc(100vh - 180px)' },
                  marginTop: 0
                }}>
                  <Box sx={{
                    position: { xs: 'relative', md: 'absolute' },
                    top: { md: 24 },
                    left: { md: 24 },
                    zIndex: 1,
                    mb: { xs: 2, md: 0 },
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: { md: 'flex-start' }
                  }}>
                    <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
                      Back
                    </BackButton>
                  </Box>

                  <Box sx={{ width: '100%', maxWidth: 920, mx: 'auto', pt: 0 }}>
                    <Container maxWidth="lg" sx={{ py: 1, pt: 2, px: { xs: 2, md: 4 } }}>
                      <Box sx={{ textAlign: 'center', mb: 1 }}>
                        <Typography 
                          variant="h2" 
                          component="h2" 
                          sx={{ 
                            mb: 1.5, 
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            color: 'text.secondary',
                            fontSize: { xs: '1.75rem', md: '2.25rem' }
                          }}
                        >
                          Personal & Family Login
                        </Typography>
                        
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            color: 'text.secondary',
                            fontWeight: 400,
                            maxWidth: 400,
                            mx: 'auto',
                            lineHeight: 1.4,
                            mb: 2,
                            fontSize: { xs: '0.95rem', md: '1.1rem' }
                          }}
                        >
                          Sign in to your personal account
                        </Typography>

                        <Box 
                          sx={{ 
                            width: 50, 
                            height: 3, 
                            background: 'linear-gradient(90deg, #2A9235, #4CAF50)',
                            borderRadius: 2,
                            mx: 'auto'
                          }} 
                        />
                      </Box>

                      {/* Loading state */}
                      {isLoading ? (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          py: 8 
                        }}>
                          <CircularProgress size={40} sx={{ color: '#2A9235', mb: 2 }} />
                          <Typography>Loading...</Typography>
                        </Box>
                      ) : (
                        <>
                          {/* Social Login Options */}
                          <StyledPaper>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                mb: 2, 
                                fontWeight: 600, 
                                color: 'text.secondary',
                                textAlign: 'center',
                                fontSize: '1.1rem'
                              }}
                            >
                              Quick login with social accounts
                            </Typography>
                            
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12}>
                                <StyledSocialButton
                                  onClick={googleSignIn}
                                  disabled={isLoading}
                                >
                                  <img 
                                    src={googleicon} 
                                    alt="Google" 
                                    className="social-logo"
                                    style={{ 
                                      height: 24,
                                      width: 24,
                                      transition: 'transform 0.3s ease'
                                    }} 
                                  />
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    Continue with Google
                                  </Typography>
                                </StyledSocialButton>
                              </Grid>

                              <Grid item xs={12}>
                                <StyledSocialButton
                                  onClick={appleSignIn}
                                  disabled={isLoading}
                                >
                                  <img 
                                    src={appleicon} 
                                    alt="Apple" 
                                    className="social-logo"
                                    style={{ 
                                      height: 24,
                                      width: 24,
                                      transition: 'transform 0.3s ease'
                                    }} 
                                  />
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    Continue with Apple
                                  </Typography>
                                </StyledSocialButton>
                              </Grid>
                            </Grid>
                          </StyledPaper>

                          {/* Email/Password Login */}
                          <StyledPaper>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                mb: 2, 
                                fontWeight: 600, 
                                color: 'text.secondary',
                                textAlign: 'center',
                                fontSize: '1.1rem'
                              }}
                            >
                              or continue with email
                            </Typography>
                            <Grid container spacing={2.5}>
                              <Grid item xs={12}>
                                <StyledTextField
                                  fullWidth
                                  label="Email Address *"
                                  type="email"
                                  placeholder="Enter your email address"
                                  value={email}
                                  onChange={(e) => validateEmail(e.target.value)}
                                  error={!!emailError}
                                  helperText={emailError}
                                  InputProps={{
                                    startAdornment: <EmailIcon sx={{ color: '#2A9235', mr: 1.5 }} />
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <StyledTextField
                                  fullWidth
                                  label="Password *"
                                  type={showPassword ? 'text' : 'password'}
                                  placeholder="Enter your password"
                                  value={password}
                                  onChange={(e) => validatePassword(e.target.value)}
                                  error={!!passwordError}
                                  helperText={passwordError}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <LockIcon sx={{ color: '#2A9235', mr: 1.5 }} />
                                      </InputAdornment>
                                    ),
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                                          onClick={handleToggleShowPassword}
                                          onMouseDown={handleMouseDownPassword}
                                          edge="end"
                                          size="small"
                                        >
                                          {showPassword ? (
                                            <VisibilityOff sx={{ color: '#2A9235' }} />
                                          ) : (
                                            <Visibility sx={{ color: '#2A9235' }} />
                                          )}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                        sx={{
                                          '&.Mui-checked': {
                                            color: '#2A9235',
                                          }
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                        Remember me
                                      </Typography>
                                    }
                                  />
                                  
                                  <Link
                                    href="/forgot-password"
                                    sx={{
                                      color: '#2A9235',
                                      textDecoration: 'none',
                                      fontSize: '0.9rem',
                                      fontWeight: 500,
                                      '&:hover': {
                                        textDecoration: 'underline',
                                      }
                                    }}
                                  >
                                    Forgot Password?
                                  </Link>
                                </Box>
                              </Grid>
                            </Grid>

                            <StyledButton
                              fullWidth
                              size="large"
                              onClick={handleEmailLogin}
                              disabled={!isFormValid() || isLoading}
                              sx={{ mt: 3 }}
                            >
                              {isLoading ? (
                                <>
                                  <CircularProgress size={18} sx={{ mr: 2, color: 'white' }} />
                                  Logging In...
                                </>
                              ) : (
                                'Log In'
                              )}
                            </StyledButton>
                          </StyledPaper>

                          {/* Sign Up Link */}
                          <Box sx={{ 
                            textAlign: 'center',
                            mt: 3,
                            p: 2,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                            border: '1px solid #e2e8f0'
                          }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontSize: '0.95rem',
                                color: 'text.secondary'
                              }}
                            >
                              Don't have an account?{' '}
                              <Link
                                href="/signup"
                                sx={{
                                  color: '#2A9235',
                                  textDecoration: 'none',
                                  fontWeight: 600,
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  }
                                }}
                              >
                                Sign Up
                              </Link>
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Container>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </section>
      </main>

      {/* Modals - matching LoginSub.tsx */}
      <CustomModal
        isModalVisible={isModalVisible}
        onPress={hideModal}
        buttonText="OK"
        modalTitle="Email Verification Required"
        text="Please verify your email address before logging in."
        icon={true}
      />

      <CustomNoResponseModal
        isModalVisible={isQuestionModalVisible}
        onPress={hideModal}
        buttonText="OK"
        modalTitle="Error"
        text={apiErrorMessage}
      />

      <Footer />
    </>
  );
};

export default IndividualLogin;
